import { EncryptedDto } from '@dtos/encrypted-dto';
import {
    RemoteI9Details,
    RepresentativeCandidateDetails
} from '@dtos/remote-i9-details';
import { OnboardingDetailsRequestEvent } from '@models/onboarding-review/onboarding-details-request-event';
import { ISection1CandidateDetail } from '@models/onboarding-review/section1-candidate-detail';
import { IOnboardingDetailsRequestService } from '@services/onboarding-review/onboarding-details-request-service';
import { IPersonPidEncryptionService } from '@services/onboarding-review/person-pid-encryption-service';
import { ISection1CandidateDetailStore } from '@stores/onboarding-review/section1-candidate-detail-store';
import {
    mapValues,
    pick
} from 'lodash';
import { Subscription } from 'rxjs';

export interface ISection1CandidateDetailUpdater {
    initialise(): void;
    cleanup(): void;
}

/**
 * Logic layer entity that responds to onboarding details request events for candidate detail store
 */
export class Section1CandidateDetailUpdater implements ISection1CandidateDetailUpdater {
    private requestEventsSubscription: Subscription = Subscription.EMPTY;

    constructor(
        private store: ISection1CandidateDetailStore,
        private personPidEncryptionService: IPersonPidEncryptionService,
        private onboardingDetailsRequestService: IOnboardingDetailsRequestService
    ) {
    }
    initialise() {
        this.requestEventsSubscription = this.onboardingDetailsRequestService
            .requestEvents
            .subscribe(this.listenToRequestEvents);
    }

    cleanup() {
        // clean up event subscriptions, memory, etc
        this.requestEventsSubscription.unsubscribe();
    }

    listenToRequestEvents = (event: OnboardingDetailsRequestEvent) => {
        if (event.type === 'pending') {
            this.handleInitialise();
            return;
        }

        if (event.type === 'success') {
            this.handleSuccess(event.dto);
            return;
        }
    };

    handleInitialise = () => {
        this.store.setDetails({
            apartmentNumber: '',
            candidateSignature: '',
            citizenshipStatus: '',
            city: '',
            countryOfIssuance: '',
            dateOfBirth: '',
            emailAddress: '',
            expirationDate: '',
            firstName: '',
            foreignPassportNumber: '',
            formI94AdmissionNumber: '',
            lastName: '',
            middleInitial: '',
            otherLastNames: '',
            permanentResidentNumber: '',
            phoneNumber: '',
            section1SignedOn: '',
            socialSecurityNumber: '',
            state: '',
            streetAddress: '',
            zipCode: ''
        });
    };

    handleSuccess = ({ candidateDetails }: RemoteI9Details) => {
        // decrypt pids
        type EncryptedProperties<T> = {
            [K in keyof T]: T[K] extends EncryptedDto ? K : never;
        }[keyof T];
        type EncryptedCandidateDetails = EncryptedProperties<RepresentativeCandidateDetails>;
        const detailsToDecrypt = pick<RepresentativeCandidateDetails, EncryptedCandidateDetails>(
            candidateDetails, [
                'dateOfBirth',
                'socialSecurityNumber',
                'permanentResidentNumber',
                'formI94AdmissionNumber',
                'foreignPassportNumber'
            ]
        );
        const decryptedDetails = mapValues(
            detailsToDecrypt,
            encryptedDto => this.personPidEncryptionService.decryptDto(encryptedDto)
        );

        // convert dto to model
        const model: ISection1CandidateDetail = {
            ...decryptedDetails,
            streetAddress: candidateDetails?.address?.streetAddress ?? null,
            apartmentNumber: candidateDetails?.address?.apartmentNumber ?? null,
            citizenshipStatus: candidateDetails.citizenshipStatus,
            city: candidateDetails?.address?.city ?? null,
            countryOfIssuance: candidateDetails.foreignPassportCountryOfIssuance,
            emailAddress: candidateDetails.emailAddress,
            expirationDate: candidateDetails.citizenshipExpiryDate,
            firstName: candidateDetails.firstName,
            candidateSignature: candidateDetails.candidateSignature,
            lastName: candidateDetails.lastName,
            middleInitial: candidateDetails.middleInitial,
            otherLastNames: candidateDetails.otherLastNames,
            phoneNumber: candidateDetails.phoneNumber,
            state: candidateDetails?.address?.state ?? null,
            zipCode: candidateDetails?.address?.zipCode ?? null,
            section1SignedOn: candidateDetails.section1SignedOn
        };

        // update store with model
        this.store.setDetails(model);
    };
}



// WEBPACK FOOTER //
// ./src/stores/onboarding-review/section1-candidate-detail-updater.ts